import { useToast } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { API_ROUTES } from "definitions/constants/routeConstants";
import apiClient from "services/ApiClient";
import { taskKeys } from "features/Resources/Task/hooks";
import { userKeys } from "features/User/hooks";

interface UseSendMessageArgs {
  onClose?: () => void;
  onSuccess?: () => void;
  onError?: () => void;
  transformData?: (data: any) => object;
}

export function useSendMessage({ transformData = (data: any) => data, onSuccess, onError, onClose }: UseSendMessageArgs) {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => apiClient.post<{ id: string }>(API_ROUTES.messageServices.messages.base, { message: { ...transformData(data) } }),
    onSuccess: (res, variables) => {
      // Invalidate task queries if task_ids were included
      const transformedData = transformData(variables);
      if ((transformedData as any).taskIds?.length > 0) {
        // Invalidate all task-related queries to ensure UI updates
        queryClient.invalidateQueries({ queryKey: taskKeys.all });
      }

      toast({
        title: "Message successfully sent.",
      });
      onSuccess?.();
      onClose?.();
    },
    onError: (e: any) => {
      console.log(e);

      if (e?.data?.errorCode === "slack_reauth_required") {
        if (e.data.senderType === "user") {
          // Backend cleared the user's OAuth identity. Invalidate currentUser so
          // sendMessageAuthorized flips to false and the composer shows the
          // re-auth form in place of the send UI.
          queryClient.invalidateQueries({ queryKey: userKeys.current() });
          toast({
            title: "Slack connection expired",
            description: "Please reconnect Slack to send messages on your behalf.",
            status: "warning",
          });
        } else {
          toast({
            title: "Slack workspace disconnected",
            description: "Your company's Slack app needs to be reinstalled by an admin.",
            status: "error",
          });
        }
      } else if (e?.errors?.[0]?.includes("not found in")) {
        toast({
          title: "Slack User Not Found",
          description: "This person doesn't have a Slack account connected. Please ask them to connect their Slack account first.",
          status: "error",
        });
      } else {
        toast({
          title: "Error",
          description: "There was an error sending the message, please try again.",
          status: "error",
        });
      }
      onError?.();
    },
  });
}

export { useStakeholderGroups } from "./hooks/useStakeholderGroups";
export { useReminderRows } from "./hooks/useReminderRows";
